import Alpine from 'alpinejs'
import { sanitizeDigitsInput } from './utils'

export const initRangeSlider = (): void => {
    Alpine.data('rangeSlider', (filterKey: string, min: number, max: number) => ({
        filterKey,
        parentElem: null as HTMLElement | null,
        absoluteMin: min,
        absoluteMax: max,
        // Step size is at least 1, should be in steps of either 100, 1000, etc. depending on the range
        stepSize: Math.round((max - min) / 100) > 1000 ? 1000 : Math.round((max - min) / 100) > 100 ? 100 : 1,
        minValue: min,
        maxValue: max,
        minInputChanged: false,
        maxInputChanged: false,

        init() {
            // Initialize from URL parameters if they exist
            const urlParams = new URLSearchParams(window.location.search)

            this.parentElem = this.$el

            // Look for range parameters in URL like: ?price_min=1000&price_max=5000
            const minParam = urlParams.get(`${this.filterKey}_min`)
            const maxParam = urlParams.get(`${this.filterKey}_max`)

            if (minParam) {
                this.minValue = parseInt(minParam)
            }
            if (maxParam) {
                this.maxValue = parseInt(maxParam)
            }

            this.loadValuesFromURL()
        },

        loadValuesFromURL() {
            const urlParams = new URLSearchParams(window.location.search)

            // Look for parameters in format: price[min][value]= and price[max][value]=
            urlParams.forEach((_value, key) => {
                // Match pattern: filterKey[min][number]= or filterKey[max][number]=
                const minMatch = key.match(new RegExp(`^${this.filterKey}\\[min\\]\\[(\\d+)\\]$`))
                const maxMatch = key.match(new RegExp(`^${this.filterKey}\\[max\\]\\[(\\d+)\\]$`))

                if (minMatch) {
                    const minFromURL = parseInt(minMatch[1])
                    // Set the actual input value
                    const minInput = this.$el.querySelector('.vlp__filters-range-slider--min') as HTMLInputElement
                    if (minInput) {
                        minInput.value = minFromURL.toString()
                        this.minValue = minFromURL
                        this.minInputChanged = true
                    }
                }

                if (maxMatch) {
                    const maxFromURL = parseInt(maxMatch[1])
                    // Set the actual input value
                    const maxInput = this.$el.querySelector('.vlp__filters-range-slider--max') as HTMLInputElement
                    if (maxInput) {
                        maxInput.value = maxFromURL.toString()
                        this.maxValue = maxFromURL
                        this.maxInputChanged = true
                    }
                }
            })
        },

        validate() {
            // Prevent min from exceeding max, but only if it doesn't go below absoluteMin
            if (this.minValue > this.maxValue - 1 && this.maxValue - 1 >= this.absoluteMin) {
                this.minValue = this.maxValue - 1
            }

            // Prevent max from going below min, but only if it doesn't exceed absoluteMax
            if (this.maxValue < this.minValue + 1 && this.minValue + 1 <= this.absoluteMax) {
                this.maxValue = this.minValue + 1
            }
        },

        updateMinValue(event: Event) {
            const minInput = event.target as HTMLInputElement
            if (minInput) {
                const newMin = parseInt(minInput.value)
                if (!isNaN(newMin)) {
                    // Adjust newMin to match the stepSize
                    let adjustedMin = Math.floor(newMin / this.stepSize) * this.stepSize

                    if (adjustedMin < newMin) {
                        adjustedMin += this.stepSize
                    }

                    if (adjustedMin < this.absoluteMin) {
                        adjustedMin = this.absoluteMin
                    }

                    minInput.value = adjustedMin.toString()

                    const slider = this.parentElem?.querySelector('.vlp__filters-range-slider--min') as HTMLInputElement
                    if (slider) {
                        slider.value = adjustedMin.toString()
                        this.minValue = adjustedMin
                    }

                    this.minInputChanged = true
                    this.validate()
                    this.onSliderChange()
                } else if (minInput.value === '') {
                    this.minInputChanged = false
                }
            }
        },

        updateMaxValue(event: Event) {
            const maxInput = event.target as HTMLInputElement
            if (maxInput) {
                const newMax = parseInt(maxInput.value)

                if (!isNaN(newMax)) {
                    // Adjust newMax to match the stepSize
                    let adjustedMax = Math.floor(newMax / this.stepSize) * this.stepSize

                    if (adjustedMax < newMax) {
                        adjustedMax += this.stepSize
                    }

                    if (adjustedMax > this.absoluteMax) {
                        adjustedMax = this.absoluteMax
                    }

                    maxInput.value = adjustedMax.toString()

                    // Retrieve the slider element to update its value
                    const slider = this.parentElem?.querySelector('.vlp__filters-range-slider--max') as HTMLInputElement
                    if (slider) {
                        slider.value = adjustedMax.toString()
                        this.maxValue = adjustedMax
                    }
                    this.maxInputChanged = true
                    this.validate()
                    this.onSliderChange()
                } else if (maxInput.value === '') {
                    this.maxInputChanged = false
                }
            }
        },

        markMinInputChanged(event: Event) {
            this.minInputChanged = true
            sanitizeDigitsInput(event.target as HTMLInputElement)
        },

        markMaxInputChanged(event: Event) {
            this.maxInputChanged = true
            sanitizeDigitsInput(event.target as HTMLInputElement)
        },

        clearMinInputIfUnchanged(event: Event) {
            const input = event.target as HTMLInputElement
            if (!this.minInputChanged && input.value !== '') {
                input.value = ''
            }
        },

        restoreMinInputIfEmpty(event: Event) {
            const input = event.target as HTMLInputElement
            if (!this.minInputChanged && input.value === '') {
                input.value = this.minValue.toString()
            }
        },

        clearMaxInputIfUnchanged(event: Event) {
            const input = event.target as HTMLInputElement
            if (!this.maxInputChanged && input.value !== '') {
                input.value = ''
            }
        },

        restoreMaxInputIfEmpty(event: Event) {
            const input = event.target as HTMLInputElement
            if (!this.maxInputChanged && input.value === '') {
                input.value = this.maxValue.toString()
            }
        },

        resetSlider() {
            this.minValue = this.absoluteMin
            this.maxValue = this.absoluteMax
            this.minInputChanged = false
            this.maxInputChanged = false

            const minInput = this.$el.querySelector('.vlp__filters-range-slider--min') as HTMLInputElement
            const maxInput = this.$el.querySelector('.vlp__filters-range-slider--max') as HTMLInputElement

            if (minInput) minInput.value = this.absoluteMin.toString()
            if (maxInput) maxInput.value = this.absoluteMax.toString()
        },

        onSliderChange() {
            // Trigger parent's applyFilters
            this.$dispatch('range-slider-change', {
                filterKey: this.filterKey,
                min: this.minValue,
                max: this.maxValue,
            })
        },

        get fillStyle(): string {
            const minPercent = ((this.minValue - this.absoluteMin) / (this.absoluteMax - this.absoluteMin)) * 100
            const maxPercent = ((this.maxValue - this.absoluteMin) / (this.absoluteMax - this.absoluteMin)) * 100

            return `left: ${minPercent}%; right: ${100 - maxPercent}%;`
        },
    }))
}
